import { useGetList, type Identifier } from "ra-core";
import { useCallback } from "react";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { invokeSend, useLive } from "../whatsapp/useWhatsapp";
import type { WaMessage } from "../whatsapp/whatsapp";
import type { SocialThread } from "./social";

/** Instagram and Messenger conversations, live. */
export const useSocialInbox = (filter: Record<string, unknown> = {}) => {
  const {
    data = [],
    refetch,
    isPending,
  } = useGetList<SocialThread>(
    "gula_social_inbox",
    {
      filter,
      pagination: { page: 1, perPage: 500 },
      sort: { field: "last_message_at", order: "DESC" },
    },
    { refetchInterval: 60_000 },
  );
  const reload = useCallback(() => {
    refetch();
  }, [refetch]);
  useLive("gula_social_threads", reload);
  return { threads: data, isPending, reload };
};

/** Messages of one Instagram or Messenger conversation (same shape as WhatsApp). */
export const useSocialMessages = (threadId?: Identifier | null) => {
  const {
    data = [],
    refetch,
    isPending,
  } = useGetList<WaMessage>(
    "gula_social_messages",
    {
      filter: { thread_id: threadId },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "created_at", order: "ASC" },
    },
    { enabled: threadId != null, refetchInterval: 60_000 },
  );
  const reload = useCallback(() => {
    refetch();
  }, [refetch]);
  useLive(
    "gula_social_messages",
    reload,
    threadId != null ? `thread_id=eq.${threadId}` : undefined,
    threadId != null,
  );
  return { messages: data, isPending, reload };
};

export const markSocialRead = async (threadId: Identifier) => {
  await getSupabaseClient()
    .from("gula_social_threads")
    .update({ unread: 0 })
    .eq("id", Number(threadId))
    .gt("unread", 0);
};

export const sendSocial = (threadId: Identifier, text: string) =>
  invokeSend(
    { thread_id: Number(threadId), text },
    "No se ha podido enviar",
    "meta_send",
  );

export const sendSocialMaterial = (
  threadId: Identifier,
  materialId: Identifier,
) =>
  invokeSend(
    { thread_id: Number(threadId), material_id: Number(materialId) },
    "No se ha podido enviar el material",
    "meta_send",
  );
